import { gql } from '@apollo/client';
import { AlertTriangleIcon, CircleIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { Tooltip } from '@components/tooltip';
import { RemoteSessionStatusFragmentFragment } from '@generated/graphql';
import { Stack, Typography, useTheme } from '@mui/material';
import { MouseEventHandler } from 'react';

export const RemoteSessionStatusFragment = gql`
  fragment RemoteSessionStatusFragment on RemoteDevelopment {
    editorSessionId
    editorUrl
    status {
      state
      message
    }
  }
`;

type RemoteSessionStatusProps = {
  session?: RemoteSessionStatusFragmentFragment;
  details?: boolean;
};

export const RemoteSessionStatus = ({ session, details = false }: RemoteSessionStatusProps) => {
  const { palette } = useTheme();
  if (!session?.status?.state || session.status.state === 'DELETED') {
    return null;
  }

  if (session.status.state === 'ERROR' || session.status.state === 'FAILED') {
    const message = session.status.message || 'Unknown Error';
    return (
      <Tooltip title={message}>
        <AlertTriangleIcon color={palette.error.main} size={12} />
      </Tooltip>
    );
  }

  const indicatorColor =
    session.status.state === 'RUNNING'
      ? palette.success.main
      : session.status.state === 'STOPPED'
        ? palette.error.main
        : palette.warning.main;

  const copyToClipboard: MouseEventHandler = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(session?.editorUrl || '');
    } catch (error) {
      let message = 'Unknown Error';
      if (error instanceof Error) message = error.message;
      console.error(message);
    }
  };

  if (details) {
    return (
      <Tooltip
        title={
          <Stack gap={1}>
            <Group justifyContent="space-between" gap={4}>
              <Typography variant="caption2">Session id</Typography>
              <Typography variant="caption2">{session?.editorSessionId || '-'}</Typography>
            </Group>
            <Group justifyContent="space-between" gap={4}>
              <Typography variant="caption2">Session status</Typography>
              <Typography variant="caption2">{session?.status.state || '-'}</Typography>
            </Group>
            <Typography variant="caption2" sx={{ fontStyle: 'italic', marginX: 'auto' }}>
              Click to copy session url
            </Typography>
          </Stack>
        }
      >
        <CircleIcon onClick={copyToClipboard} color={indicatorColor} size={12} />
      </Tooltip>
    );
  }
  return <CircleIcon color={indicatorColor} size={12} />;
};
